import { DocumentFilter, TickCircle } from "iconsax-reactjs"
import { parseAsBoolean, useQueryState } from "nuqs"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"

import { BottomSheetModal } from "@/components/shared"
import { Button } from "@/components/ui"
import { EFilterStatus } from "@/modules/helpers/enum"
import {
  DEFAULT_FILTER_SUBMISSION,
  type SubmissionFilterValues,
  useFilterSubmission
} from "@/modules/helpers/hooks/shared/use-filter-submission"
import { cn } from "@/modules/helpers/hooks/shared/utils"

const ORDER_OPTIONS = [
  { key: "DESC" as const, label: "Mới nhất" },
  { key: "ASC" as const, label: "Cũ nhất" }
]

const CAMPAIGN_TYPE_OPTIONS = [
  { key: "HAS_CAST" as const, label: "Có Cast" },
  { key: "NO_CAST" as const, label: "Không Cast" }
]

const STATUS_OPTIONS = [
  { key: "ALL" as const, label: "Tất cả" },
  { key: EFilterStatus.WORKING as const, label: "Duyệt - chờ nhận hàng" },
  { key: EFilterStatus.OVERDUE as const, label: "Quá hạn" },
  { key: EFilterStatus.FINISHED as const, label: "Đã hoàn thành" },
  { key: EFilterStatus.REJECTED as const, label: "Từ chối" },
  { key: EFilterStatus.NOT_ATTACH_LINK as const, label: "Chưa gắn liên kết" },
  { key: EFilterStatus.NOT_AUTHORIZED_ORDER_SAMPLE as const, label: "Chưa ủy quyền đặt hàng" }
]

const TAG_OPTIONS = [
  { key: "ALL" as const, label: "Tất cả" },
  // { key: "ZXU" as const, label: "Z Xu" },
  // { key: "FREE_SAMPLE" as const, label: "Mẫu miễn phí" },
  { key: "SHOW_FACE" as const, label: "Show mặt" }
]

const COMMISSION_RATE_OPTIONS = [
  // { key: "HIGHEST" as const, label: "Cao nhất" },
  // { key: "LOWEST" as const, label: "Thấp nhất" },
  { key: "MORE_THAN_15", label: "Nhiều hơn 15%" },
  { key: "11_TO_14", label: "11 - 14%" },
  { key: "6_TO_10", label: "6 - 10%" },
  { key: "3_TO_5", label: "3 - 5%" }
] as const

type FilterFormValues = SubmissionFilterValues

const createDefaultFormValues = (): FilterFormValues => ({
  ...DEFAULT_FILTER_SUBMISSION
})

export function FilterSubmission() {
  const [isVisible, setIsVisible] = useQueryState(
    "filterSubmission",
    parseAsBoolean.withDefault(false)
  )
  const form = useForm<FilterFormValues>({
    defaultValues: createDefaultFormValues()
  })

  const { filter, setFilter, reset: resetFilterState, calculateFilterCount } = useFilterSubmission()

  useEffect(() => {
    form.reset(filter ?? createDefaultFormValues())
  }, [filter, form])

  const filterCount = calculateFilterCount()
  const handleApplyFilters = () => {
    form.handleSubmit((values) => {
      setFilter({ ...values })
      setIsVisible(false)
    })()
  }

  const handleResetFilters = () => {
    resetFilterState()
    form.reset(createDefaultFormValues())
  }

  return (
    <>
      <div
        className="rounded-xl bg-white w-12 h-12 border flex justify-center items-center relative"
        onClick={() => setIsVisible(true)}
      >
        {filterCount > 0 && (
          <span className="bg-red-500 text-white flex justify-center items-center text-[10px] font-medium w-4 h-4 rounded-full absolute -top-1 -right-1">
            {filterCount}
          </span>
        )}
        <DocumentFilter size={24} color="#045664" />
      </div>

      <BottomSheetModal isVisible={isVisible} setIsVisible={setIsVisible} title="Bộ lọc">
        <div className="flex flex-col gap-2 p-2 px-3">
          {/* Separator line */}
          <div className="h-px bg-gray-200 -mt-4 mb-2" />

          {/* Sắp xếp theo */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Sắp xếp theo</label>
            <Controller
              name="order"
              control={form.control}
              render={({ field }) => (
                <div className="flex gap-2">
                  {ORDER_OPTIONS.map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "text-xs rounded-lg flex items-center gap-2 px-3 py-2.5 bg-white border-gray-300 text-gray-800"
                        )}
                        onClick={() => field.onChange(item.key)}
                      >
                        {isSelected ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center">
                            <TickCircle size={12} className="text-secondary-100" variant="Bold" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-lg border border-gray-300" />
                        )}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Loại chiến dịch */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Loại chiến dịch</label>
            <Controller
              name="campaignType"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {CAMPAIGN_TYPE_OPTIONS.map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn("text-xs rounded-lg px-3 py-2", {
                          "bg-gradient-primary border-0 text-white": isSelected,
                          "bg-white border-gray-300 text-gray-800": !isSelected
                        })}
                        onClick={() => field.onChange(item.key)}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Trạng thái */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Trạng thái</label>
            <Controller
              name="status"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.slice(0, 3).map((item) => {
                      const isSelected = field.value === item.key
                      return (
                        <Button
                          key={item.key}
                          variant="outline"
                          size="sm"
                          className={cn("text-xs rounded-lg px-3 py-2", {
                            "bg-gradient-primary border-0 text-white": isSelected,
                            "bg-white border-gray-300 text-gray-800": !isSelected
                          })}
                          onClick={() => field.onChange(item.key)}
                        >
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.slice(3).map((item) => {
                      const isSelected = field.value === item.key
                      return (
                        <Button
                          key={item.key}
                          variant="outline"
                          size="sm"
                          className={cn("text-xs rounded-lg px-3 py-2", {
                            "bg-gradient-primary border-0 text-white": isSelected,
                            "bg-white border-gray-300 text-gray-800": !isSelected
                          })}
                          onClick={() => field.onChange(item.key)}
                        >
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
            />
          </div>

          {/* Lời nhắc */}
          {/* <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Lỗi nhắc</label>
            <Controller
              name="reminder"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {REMINDER_OPTIONS.map((item) => {
                      const isSelected = field.value === item.key
                      return (
                        <Button
                          key={item.key}
                          variant="outline"
                          size="sm"
                          className={cn("text-xs rounded-lg px-3 py-2", {
                            "bg-gradient-primary border-0 text-white": isSelected,
                            "bg-white border-gray-300 text-gray-800": !isSelected
                          })}
                          onClick={() => field.onChange(item.key)}
                        >
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
            />
          </div> */}

          {/* Tag */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Tag</label>
            <Controller
              name="tag"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn("text-xs rounded-lg px-3 py-2", {
                          "bg-gradient-primary border-0 text-white": isSelected,
                          "bg-white border-gray-300 text-gray-800": !isSelected
                        })}
                        onClick={() => field.onChange(item.key)}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Tỷ lệ hoa hồng */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Tỷ lệ hoa hồng</label>
            <Controller
              name="commissionRange"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex flex-wrap gap-2">
                    {COMMISSION_RATE_OPTIONS.map((item) => {
                      const isSelected = field.value === item.key
                      return (
                        <Button
                          key={item.key}
                          variant="outline"
                          size="sm"
                          className={cn("text-xs rounded-lg px-3 py-2", {
                            "bg-gradient-primary border-0 text-white": isSelected,
                            "bg-white border-gray-300 text-gray-800": !isSelected
                          })}
                          onClick={() => field.onChange(item.key)}
                        >
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <Button
              variant="outline"
              className="w-28 !h-12 text-xs border-gray-200 bg-white text-primary-800 rounded-xl"
              onClick={handleResetFilters}
            >
              Đặt lại
            </Button>
            <Button
              className="flex-1 text-xs h-12 rounded-xl bg-primary font-medium"
              onClick={handleApplyFilters}
            >
              Xem kết quả
            </Button>
          </div>
        </div>
      </BottomSheetModal>
    </>
  )
}
